import { Layout } from './layout';
import { GENES, nfc, drawRadialLabel, getClosestMutation,
  TAU, START_ANGLE, BY_GENOME, BY_MUTATION_BLOCK } from './common';
import { GENE_COLORS, LIGHT_VARIANT_NAME_COLOR, LIGHT_MUTATION_POINTER_COLOR,
  DARK_VARIANT_NAME_COLOR, DARK_MUTATION_POINTER_COLOR,
  VARIANT_FONT, MUTATION_FONT, PCT_FONT, COUNT_FONT,
  SEQUENCE_TICK_STROKE_WEIGHT, MUTATION_TICK_STROKE_WEIGHT,
  MUTATION_BLOCK_STROKE_WEIGHT,
  BAND_WIDTH,  LABEL_RADIUS_MARGIN, VARIANT_MARGIN,
  // MUTATION_LABEL_PUSH_BACK_OPACITY,
  ARC_RANGE_DESKTOP, ARC_RANGE_MOBILE,
  RADIAL_SEQUENCE_GENE_LABEL_COLOR_NUM,
  RADIAL_MUTATION_GENE_LABEL_COLOR_NUM,
  OUTER_MARGIN, SEQ_MARGIN_RADIAL, MUT_MARGIN_RADIAL } from './styles';

import { MutationArrangement } from './mutationarrangement';



export default class RadialLayout extends Layout {

  setIsMobile(isMobile) {
    super.setIsMobile(isMobile);
    this.arcRange = isMobile ? ARC_RANGE_MOBILE : ARC_RANGE_DESKTOP;
    this.angleRange = TAU * this.arcRange;
  }

  setSize(W, H) {
    super.setSize(W, H);
    this.maxRadius = Math.min(this.cx, this.cy) * (1 - OUTER_MARGIN);
  }

  positionBySequence() {
    let {geneWedges, variants, isMobile, cx, cy} = this;
    if (!variants || !variants.length) return;
    let arrangement = new MutationArrangement(this.geneWedges, this.currentMutations);
    arrangement.arrangeByPosition();
    let {genes, labelList} = arrangement;

    let smaller = Math.min(cx,cy),
      regionCount = Object.keys(genes).length,
      currentWedges = geneWedges.filter(gw=>gw.shown || !isMobile),
      angleRange = TAU * this.arcRange - (regionCount - 1) * SEQ_MARGIN_RADIAL,
      mutCount = currentWedges.reduce((total, gw)=>total + gw.aaCount -1, 0),
      angle = START_ANGLE;
    this.bandWidth = smaller * Math.min(BAND_WIDTH, 0.36 / variants.length);
    this.bandMargin = smaller * VARIANT_MARGIN;
    this.lineweight = SEQUENCE_TICK_STROKE_WEIGHT;
    let mutLookup = {};
    geneWedges.forEach((gw)=>{
      let geneData = genes[gw.name];
      if (!geneData) {
        gw.setAngles(angle, angle);
        gw.visibles.forEach(item=>{
          item.cosA = NaN;
        });
      } else {
        let {aaCount} = gw,
          labels = genes[gw.name].labels,
          arcWidth = aaCount / mutCount * angleRange,
          sa = angle,
          ea = angle + arcWidth;
        gw.setAngles(sa, ea);
        gw.visibles.forEach(item=>{
          let mutation = item.mutation,
            position = mutation.position,
            label = labels[position];
          item.angle = gw.getAngle(item.mutation);
          item.cosA = Math.cos(item.angle);
          item.sinA = Math.sin(item.angle);
          mutLookup[mutation.name] = item;
          label.setAngle(item.angle);
        });
        angle += arcWidth + SEQ_MARGIN_RADIAL;
      }
    });
    this.labelRadius = cx;
    this.setVariantPositions(mutLookup, genes, labelList);
    this.textColorNum = RADIAL_SEQUENCE_GENE_LABEL_COLOR_NUM;
    this.geneLabels = genes;
    this.labelList = labelList;
  }

  positionByMutationName() {
    let {geneWedges, variants, currentMutations, currentMutationsLookup,
      isMobile, cx, cy, maxRadius, angleRange} = this;

    if (!variants || !variants.length) return;
    // tally mutations by gene
    let geneCount = {},
      /** do not filter by gene in radial view [mark 220330] **/
      currentWedges = geneWedges.filter(gw=>gw.shown || !isMobile),
      countBasis = 0,
      genesWMutations = [];
    GENES.forEach(g=>geneCount[g] = []);
    currentMutations.forEach(item=>geneCount[item.mutation.gene].push(item));
    geneWedges.forEach(gw=>{
      let g = gw.name;
      if (currentWedges.indexOf(gw) >= 0 && geneCount[g] && geneCount[g].length) {
        countBasis += geneCount[g].length - 1;
        genesWMutations.push(g);
      }
    });
    let smaller = Math.min(cx,cy),
      bandWidth = smaller * Math.min(BAND_WIDTH, 0.25 / variants.length),
      bandMargin = smaller * VARIANT_MARGIN,
      spacePerMutation = angleRange / (countBasis + genesWMutations.length - 1),
      a = START_ANGLE,
      mutLookup = {};
    geneWedges.forEach((gw)=>{
      let geneMutations = geneCount[gw.name],
        count = geneMutations.length,
        sa = a;
      if (currentWedges.indexOf(gw) < 0 || count === 0) {
        sa -= spacePerMutation * 0.5;
        gw.setAngles(sa, sa);
        geneMutations.forEach((item)=>{
          item.cosA = NaN;
          mutLookup[item.mutation.name] = item;
        });
      } else {
        count--;
        let arcWidth = count * spacePerMutation,
          ea = sa + arcWidth;
        gw.setAngles(sa, ea);
        geneMutations.forEach((item, i)=>{
          item.angle = sa + ( i === 0 ? 0 : (arcWidth * i / count));
          item.cosA = Math.cos(item.angle);
          item.sinA = Math.sin(item.angle);
          mutLookup[item.mutation.name] = item;
        });
        a += arcWidth + spacePerMutation;
      }
    });
    this.labelRadius = cx;
    variants.forEach((variant, i)=>{
      let inner = maxRadius - (variants.length - i) * (bandWidth + bandMargin),
        outer = inner + bandWidth;
      this.labelRadius = Math.min(this.labelRadius, inner - LABEL_RADIUS_MARGIN);
      variant.setRadii(inner, outer, inner + bandWidth * 0.4);
      variant.visibles.forEach((item)=>{
        let mutation = item.mutation,
          posInfo = mutLookup[mutation.name],
          color = currentMutationsLookup[mutation.name].color;
        if (posInfo) {
          item.angle = posInfo.angle;
          item.cosA = posInfo.cosA;
          item.sinA = posInfo.sinA;
          item.color = color;
        } else {
          item.cosA = NaN;
        }
      });
    });
    this.lineweight = MUTATION_TICK_STROKE_WEIGHT;
    this.textColorNum = RADIAL_MUTATION_GENE_LABEL_COLOR_NUM;
  }




  positionByMutationLocation() {
    let {geneWedges, variants, currentMutations, currentMutationsLookup,
      isMobile, cx, cy, maxRadius, angleRange} = this;

    if (!variants || !variants.length) return;
    // tally mutations by gene
    let geneCount = {},
      /** do not filter by gene in radial view [mark 220330] **/
      currentWedges = geneWedges.filter(gw=>gw.shown || !isMobile);
    GENES.forEach(g=>geneCount[g] = []);
    // in this version, mutations at the same position have
    // the same x location
    let genePos = {};
    currentMutations.forEach(item=>{
      let mutation = item.mutation,
        gene = mutation.gene,
        pos = mutation.position;
      geneCount[gene].push(item);
      if (genePos[gene] === undefined) {
        genePos[gene] = {};
      }
      if (genePos[gene][pos] === undefined) {
        genePos[gene][pos] = [];
      }
      genePos[gene][pos].push(item);
    });
    let countBasis = 0,
      genesWMutations = [];
    geneWedges.forEach(gw=>{
      let g = gw.name;
      if (currentWedges.indexOf(gw) >= 0) {
        let keys = genePos[g] ? Object.keys(genePos[g]) : null;
        if (keys) {
          countBasis += keys.length - 1;
        } else {
          // countBasis++;
        }
        genesWMutations.push(g);
      }
    });
    let smaller = Math.min(cx,cy),
      bandWidth = smaller * Math.min(BAND_WIDTH, 0.25 / variants.length),
      bandMargin = smaller * VARIANT_MARGIN,
      spacePerMutation = angleRange / (countBasis + genesWMutations.length - 1),
      a = START_ANGLE,
      mutLookup = {};
    geneWedges.forEach((gw)=>{
      let geneMutations = geneCount[gw.name],
        sa = a;
      if (currentWedges.indexOf(gw) < 0) {
        sa -= spacePerMutation * 0.5;
        gw.setAngles(sa, sa);
        geneMutations.forEach((item)=>{
          item.cosA = NaN;
          mutLookup[item.mutation.name] = item;
        });
      } else {
        let geneMutations = geneCount[gw.name],
          count = geneMutations.length,
          arcWidth = 0;
        if (count === 0) {
          gw.setAngles(sa, sa);
          geneMutations.forEach((item)=>{
            item.angle = sa;
            item.cosA = Math.cos(sa);
            item.sinA = Math.sin(sa);
            mutLookup[item.mutation.name] = item;
          });
        } else {
          let positions = Object.keys(genePos[gw.name]).map(p=>parseInt(p));
          count = positions.length;
          arcWidth = (count-1) * spacePerMutation;
          gw.setAngles(sa, sa + arcWidth);
          if (count === 1) {
            geneMutations.forEach(item=>{
              item.angle = sa;
              item.cosA = Math.cos(sa);
              item.sinA = Math.sin(sa);
              mutLookup[item.mutation.name] = item;
            });
          } else {
            count--;
            geneMutations.forEach(item=>{
              let index = positions.indexOf(item.mutation.position);
              item.angle = sa + arcWidth * index / count;
              item.cosA = Math.cos(item.angle);
              item.sinA = Math.sin(item.angle);
              mutLookup[item.mutation.name] = item;
            });
          }
        }
        a += arcWidth + spacePerMutation;
      }
    });
    this.labelRadius = cx;
    variants.forEach((variant, i)=>{
      let inner = maxRadius - (variants.length - i) * (bandWidth + bandMargin),
        outer = inner + bandWidth;
      this.labelRadius = Math.min(this.labelRadius, inner - LABEL_RADIUS_MARGIN);
      variant.setRadii(inner, outer, inner + bandWidth * 0.4);
      variant.visibles.forEach((item)=>{
        let mutation = item.mutation,
          posInfo = mutLookup[mutation.name],
          color = currentMutationsLookup[mutation.name].color;
        if (posInfo) {
          item.angle = posInfo.angle;
          item.cosA = posInfo.cosA;
          item.sinA = posInfo.sinA;
          item.color = color;
        } else {
          item.cosA = NaN;
        }
      });
    });
    this.lineweight = MUTATION_BLOCK_STROKE_WEIGHT;
    this.textColorNum = RADIAL_MUTATION_GENE_LABEL_COLOR_NUM;
  }

  setVariantPositions(mutLookup, genes, labelList) {
    let {variants, threshold, maxRadius} = this;
    variants.forEach((variant, i)=>{
      let inner = maxRadius - (variants.length - i) * (this.bandWidth + this.bandMargin),
        outer = inner + this.bandWidth;
      this.labelRadius = Math.min(this.labelRadius, inner - LABEL_RADIUS_MARGIN)
      variant.setRadii(inner, outer, inner + this.bandWidth * 0.4);
      variant.setMutationPositionsRadial(mutLookup, genes, labelList, threshold, this.bySequence);
    });
  }

  setMutationIndex(i) {} // eslint-disable-line no-unused-vars

  draw(ctx) {
    let {variants, W, H, cx, cy, bySequence, isMobile} = this;
    if (!variants || !variants.length) return;
    ctx.clearRect(-cx, -cy, W, H);
    this.drawWedges(ctx);
    // this.drawHighlight(ctx);
    this.drawVariantMutations(ctx);
    this.drawVariantNames(ctx);
    if (bySequence === BY_GENOME) {
      this.drawGeneLabels(ctx);
    } else {
      if (!isMobile) {
        this.drawMutationLabels(ctx);
      }
      this.drawGeneLabelsOffset(ctx);
    }

  }


  drawWedges(ctx) {
    let {geneWedges, variants, bySequence, darkMode} = this;
    ctx.lineCap = 'butt';
    ctx.lineWidth = this.lineweight;
    ctx.globalAlpha = 1;
    geneWedges.forEach(gw=>{
      /** do not filter by gene in radial view [mark 220330] **/
      if (gw.shown) {
        variants.forEach(variant=>{
          variant.drawGeneWedge(ctx, bySequence, darkMode, gw);
        });
      }
    });
  }


  drawHighlight(ctx) {
    let {variants, bySequence, darkMode, isMobile,
      labelRadius, maxRadius, bandMargin, highlightGene,
      highlightMutation} = this;

    if (highlightMutation) {
      let ca = highlightMutation.cosA,
        sa = highlightMutation.sinA,
        ir = labelRadius - (bySequence === BY_GENOME ? 70 : 0),
        or = maxRadius - bandMargin;
      ctx.strokeStyle = LIGHT_MUTATION_POINTER_COLOR;
      if (darkMode) {
        ctx.strokeStyle = DARK_MUTATION_POINTER_COLOR;
      }
      ctx.globalAlpha = 0.5;
      ctx.lineWidth = this.lineweight;
      ctx.beginPath();
      ctx.moveTo(ir * ca, ir * sa);
      ctx.lineTo(or * ca, or * sa);
      ctx.stroke();
      ctx.globalAlpha = 1;
      if (isMobile) {
        const PCT_LEFT = 85,
          COUNT_LEFT = 130,
          colorConfig = GENE_COLORS[highlightGene],
          pctColor = darkMode ? colorConfig.dark_color_1 : colorConfig.light_color_1,
          countColor = darkMode ? colorConfig.dark_color_4 : colorConfig.light_color_4,
          mutation = highlightMutation.mutation,
          mName = mutation.name;
        // crosscutURL = `https://banyan.fathom.info/crosscut/?mutations=${mName}`;
        variants.forEach(variant=>{
          let pct = variant.getPercent(mName),
            count = variant.getCount(mName),
            {labelY} = variant;
          labelY += 6;
          if (variant.count === 1) {
            /* this is an uploaded sequence */
          } else {
            ctx.fillStyle = pctColor;
            ctx.font = PCT_FONT;
            ctx.fillText(`${Math.round(pct* 100)  }%`, PCT_LEFT, labelY);
            ctx.fillStyle = countColor;
            ctx.font = COUNT_FONT;
            ctx.fillText(`(${nfc(count)})`, COUNT_LEFT, labelY);
          }
        });
      }
    }
  }


  drawVariantMutations(ctx) {
    this.variants.forEach(variant=>{
      variant.drawMutationsRadial(ctx);
    });
  }

  drawGeneLabels(ctx) {
    let {geneWedges, darkMode, isMobile, labelRadius, textColorNum } = this;
    let textColorKey = (darkMode ? 'dark_color_' : 'light_color_') + textColorNum;
    ctx.lineCap = 'butt';
    ctx.lineWidth = this.lineweight;
    ctx.globalAlpha = 1;
    ctx.font = MUTATION_FONT;
    geneWedges.forEach(gw=>{
      /** do not filter by gene in radial view [mark 220330] **/
      if (gw.shown) {
        let {sa, cs, ss, label} = gw,
          gene = gw.name;
        ctx.fillStyle = GENE_COLORS[gene][textColorKey];
        if (!isMobile) {
          drawRadialLabel(ctx, label, sa, cs, ss, 0, 0, labelRadius);
        }
      }
    });
  }

  drawGeneLabelsOffset(ctx) {
    let {geneWedges, bySequence, darkMode, labelRadius, textColorNum } = this;
    let textColorKey = (darkMode ? 'dark_color_' : 'light_color_') + textColorNum;

    ctx.lineCap = 'butt';
    ctx.lineWidth = this.lineweight;
    ctx.font = MUTATION_FONT;
    geneWedges.forEach(gw=>{
      /** do not filter by gene in radial view [mark 220330] **/
      if (gw.shown) {
        let label = gw.label,
          gene = gw.name;
        ctx.fillStyle = GENE_COLORS[gene][textColorKey];
        let {mutation, angle, cosA, sinA} = gw.visibles[0],
          mutLabel = bySequence === BY_MUTATION_BLOCK ? mutation.posLabel : mutation.label,
          tw = ctx.measureText(mutLabel).width;
        drawRadialLabel(ctx, label, angle, cosA, sinA, tw + 10, 0, labelRadius, true);
      }
    });
  }

  drawMutationLabels(ctx) {
    let {geneWedges, bySequence, darkMode,
      labelRadius, textColorNum } = this;
    let textColorKey = (darkMode ? 'dark_color_' : 'light_color_') + textColorNum;
    ctx.lineCap = 'butt';
    ctx.lineWidth = this.lineweight;
    ctx.globalAlpha = 1;
    ctx.font = MUTATION_FONT;
    geneWedges.forEach(gw=>{
      /** do not filter by gene in radial view [mark 220330] **/
      if (gw.shown) {
        let gene = gw.name;
        ctx.fillStyle = GENE_COLORS[gene][textColorKey];
        gw.visibles.forEach(({mutation, angle, cosA, sinA})=>{
          let mutLabel = bySequence === BY_MUTATION_BLOCK ? mutation.posLabel : mutation.label;
          drawRadialLabel(ctx, mutLabel, angle, cosA, sinA, 0, 0, labelRadius);
        });
      }
    });
  }


  drawVariantNames(ctx) {
    let {variants, darkMode} = this;
    variants.forEach(variant=>{
      let {labelY} = variant,
        textX = 12;
      ctx.font = VARIANT_FONT;
      ctx.fillStyle = LIGHT_VARIANT_NAME_COLOR;
      if (darkMode) {
        ctx.fillStyle = DARK_VARIANT_NAME_COLOR;
      }
      ctx.fillText(variant.label, textX, labelY + 6);
    });
  }




  getMutationAt(ex, ey) {
    let {cx, cy, bySequence, maxRadius, isMobile, variants, geneWedges, currentMutations } = this;
    let mx = ex - cx,
      my = ey - cy,
      d2 = mx * mx + my * my,
      hgene = null,
      hpos = -1,
      hring = null,
      hmut = null,
      margin = (bySequence === BY_GENOME ? SEQ_MARGIN_RADIAL : MUT_MARGIN_RADIAL);
    if (d2 <= maxRadius * maxRadius) {
      let angle = Math.atan2(my, mx);
      while (angle < START_ANGLE) angle += TAU;
      let d = Math.sqrt(d2);
      let variantFilter = ({inner, outer})=>inner <= d && d <= outer,
        geneFnc = gw=>{return (gw.shown || !isMobile) && angle >= gw.sa - margin * 0.5 && angle <= gw.ea + margin},
        posFnc = gw=>Math.round((angle - gw.sa) / gw.angleRange * gw.aaCount),
        proxFnc = item=>Math.abs(item.angle - angle);
      let result = getClosestMutation(variantFilter, geneFnc, posFnc, proxFnc,
        variants, geneWedges, currentMutations);
      hring = result.hring;
      hgene = result.hgene;
      hpos = result.hpos;
      hmut = result.hmut;
    }
    return {hgene, hpos, hmut, hring};
  }

}


